import type { FC } from 'react';
import { useMemo } from 'react';
import { useUserStore } from '@Auth/store';
import { MODALS } from '@constants/index';
import { useEntityStore } from '@ContractBuilder/store';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import { groupByParent } from '@helpers/groupBlocksByParent';
import { useModalDispatch } from '@hooks/useModalProvider';
import { ListTypesToggle } from '@pages/User/components/ListTypesToggle';
import { useBlocks } from '@queries/fetchBlocks';
import { useAllTeams } from '@queries/fetchTeams';
import type { Action, PlainAction } from '@root/@types/types';
import { isNonNullish } from '@root/helpers';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { useEffectOnce } from '@src/hooks';

import withMenuLayout from '../../layout/withMenuLayout';

import { DownloadCSVButton } from './components/DownloadCSVButton';
import { getClausesTableColumns } from './helpers/columns/getClausesTableColumns';
import { ReactTable } from './components';
import { useDataIsEmpty } from './hooks';

import './helpers/columns/clauses.css';

const initialFilters = [{ id: 'deleted_at', value: ['Active'] }];
const initialColumnsVisibility = {
  class_of_business: false,
  created_at: false,
  created_by: false,
  id: false,
  inception_date: false,
  last_updated_by: false,
  teams: false,
  toggleSubRows: false,
};

export const Clauses: FC = () => {
  const { resetState } = useEntityStore(({ resetState }) => ({ resetState }));
  const { user } = useUserStore(({ user }) => ({ user }));
  const { showModal } = useModalDispatch();

  const { data, status } = useBlocks({ type: 'clause' });
  const { data: teams = [], isLoading: isTeamsLoading } = useAllTeams();

  const isEmpty = useDataIsEmpty(data, status);

  const handleCreateBlockClick = () => {
    showModal(MODALS.CREATE_LIBRARY_BLOCK, {});
  };

  useEffectOnce(() => {
    resetState();
  });

  const columns = getClausesTableColumns({ user, teams });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New Clause',
    onClick: handleCreateBlockClick,
  };

  const actions: Action[] = [
    createEntityAction,
    isPPLFrontend ? undefined : <DownloadCSVButton key="download-csv" type="clauses" />,
  ].filter(isNonNullish);

  const dataGroupedByParents = useMemo(() => groupByParent(data), [data]);

  return (
    <ReactTable
      actions={actions}
      columns={columns}
      columnVisibility={initialColumnsVisibility}
      columnPinning={{ right: ['edit'] }}
      createEntityAction={createEntityAction}
      entityName="clause"
      filteredData={(data ?? []) as LibraryBlockTypeForListPage[]}
      expandableData={dataGroupedByParents ?? []}
      initialSorting={[{ id: 'updated_at', desc: true }]}
      initialFilters={initialFilters}
      status={status}
      renderCustomElements={(isGroupByParent, setIsGroupByParent) =>
        function toggleListTypes() {
          return <ListTypesToggle isGroupByParent={isGroupByParent} setIsGroupByParent={setIsGroupByParent} />;
        }
      }
      isEmpty={isEmpty}
      isLoading={isTeamsLoading}
    />
  );
};

export const UserClausesPage = withMenuLayout(Clauses);
